/**
 * 삼성전자 감사 페스티벌 — 디지털 온누리상품권 증정 설계
 * 행사 기간: 2026.06.08 ~ 07.02 (이 기간에만 제공)
 *
 * 증정 금액은 모델·용량별 고정 금액이며 **통신사는 무관**하다.
 * 출처: 삼성전자 감사 페스티벌 증정금액표 (모델 코드 SM-xxxx 기준).
 * 여기서는 와이즐리가 판매하는 갤럭시 상품(productCode)만 매핑한다.
 * (아이폰은 본 행사 대상 아님 → 매핑 없음)
 */

import type { StorageOption } from "@/config/mobile-optimal-config";

export const ONURI_GIFT_EVENT_START = new Date("2026-06-08T00:00:00+09:00");
export const ONURI_GIFT_EVENT_END = new Date("2026-07-02T00:00:00+09:00");

/** UI 노출용 기간 라벨 */
export const ONURI_GIFT_EVENT_PERIOD_LABEL = "2026.06.08~07.02";
/** 짧은 마감 라벨 (배지용) */
export const ONURI_GIFT_EVENT_SHORT_LABEL = "행사 기간 한정 ~7.2";

/** 현재 행사 기간 내인지 (KST 기준 시각 비교) */
export function isOnuriGiftEventActive(now: Date = new Date()): boolean {
  return now >= ONURI_GIFT_EVENT_START && now <= ONURI_GIFT_EVENT_END;
}

/**
 * productCode → 용량별 디지털 온누리상품권 증정 금액(원). 통신사 무관.
 * 우측 주석은 삼성 증정금액표의 모델 코드(SM-xxxx).
 */
const ONURI_GIFT_BY_PRODUCT: Record<string, Partial<Record<StorageOption, number>>> = {
  GALAXYS25: { "256GB": 211000, "512GB": 251000 }, // SM-S931N
  GALAXYS25EDGE: { "256GB": 263000, "512GB": 288000 }, // SM-S937N
  GALAXYS25FE: { "128GB": 166000, "256GB": 166000 }, // SM-S731N (용량 무관 166,000)
  GALAXYS25PLUS: { "256GB": 262000, "512GB": 263000 }, // SM-S936N
  GALAXYS25ULTRA: { "256GB": 320000, "512GB": 357000 }, // SM-S938N
  GALAXYS26: { "256GB": 235000, "512GB": 283000 }, // SM-S942N
  GALAXYS26PLUS: { "256GB": 337000, "512GB": 374000 }, // SM-S947N
  GALAXYS26ULTRA: { "256GB": 337000, "512GB": 374000 }, // SM-S948N
  GALAXYZFLIP7: { "256GB": 263000, "512GB": 295000 }, // SM-F766N
  GALAXYZFOLD7: { "256GB": 429000, "512GB": 477000 }, // SM-F966N
  SMA175N: { "128GB": 58000 }, // SM-A175N (갤럭시 A17, 키즈폰)
};

/** 기간 무관, 모델·용량 매핑 금액만 조회 (없으면 null) */
export function getOnuriGiftAmount(productCode?: string, storage?: string): number | null {
  if (!productCode || !storage) return null;
  const byStorage = ONURI_GIFT_BY_PRODUCT[productCode.toUpperCase()];
  if (!byStorage) return null;
  const amount = byStorage[storage as StorageOption];
  return typeof amount === "number" ? amount : null;
}

/** 행사 기간 내 + 매핑 금액 존재 시에만 금액 반환 (UI 노출 판단용) */
export function getActiveOnuriGiftAmount(
  productCode?: string,
  storage?: string,
  now: Date = new Date(),
): number | null {
  if (!isOnuriGiftEventActive(now)) return null;
  return getOnuriGiftAmount(productCode, storage);
}
